<script lang="ts" setup>
import {ref, computed} from "vue"
import {route} from "@/common/utils/ziggy"
import {useEndpoint} from "@/common/utils/fetch"
import {utcToLocalYMD} from "@/common/utils";

type Quote = {
  id: number
  date: string | null
  school: string | null
  seats: number | null
  email: string | null
  recaptcha_score: number | null
}

type Pagination = {
  page: number,
  per_page: number
  total: number
}

const props = defineProps<{
  quotes: Quote[],
  pagination: Pagination
}>()

const showingFrom = computed(() => props.pagination.total === 0 ? 0 : (props.pagination.page - 1) * props.pagination.per_page + 1)
const showingTo = computed(() => Math.min(props.pagination.page * props.pagination.per_page, props.pagination.total))
const totalPages = computed(() => Math.ceil(props.pagination.total / props.pagination.per_page))

const pendingQuotes = ref<Quote[]>([...props.quotes])
const processingIds = ref<number[]>([])

async function setQuoteStatus(quote: Quote, status: string) {
  const endpoint = useEndpoint(route('admin.quotes.set_status', {quote: quote.id}))
  processingIds.value = [...processingIds.value, quote.id]
  try {
    await endpoint.post({status}).execute()
    pendingQuotes.value = pendingQuotes.value.filter(q => q.id !== quote.id)
  } catch (error) {
    console.error(`Failed to process quote:`, error)
  } finally {
    processingIds.value = processingIds.value.filter(id => id !== quote.id)
  }
}

function formatScore(score: number | null): string {
  if (score === null) return '—'
  return score.toFixed(2)
}

function scoreClass(score: number | null): string {
  if (score === null) return ''
  if (score >= 0.7) return 'bg-green'
  if (score >= 0.3) return 'bg-yellow'
  return 'bg-pink'
}

function paginationLink(page: number) {
  return route('admin.quotes.index', {page})
}
</script>

<template>
  <div class="container-fluid">
    <h2 class="mb-4">Quote Approval</h2>

    <div v-if="pendingQuotes.length === 0" class="alert alert-info">
      No pending quotes to review.
    </div>

    <template v-else>

      <div class="d-flex justify-content-around align-items-center mb-2">
        <div>
          <div class="text-center">Showing {{ showingFrom }} – {{ showingTo }} of {{ pagination.total }}</div>
          <b-pagination-nav :value="pagination.page" :link-gen="paginationLink" :number-of-pages="totalPages" />
        </div>
      </div>

      <table class="table">
        <thead>
        <tr>
          <th></th>
          <th class="text-nowrap">Date</th>
          <th>School</th>
          <th>Seats</th>
          <th>Email</th>
          <th>Score</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="quote in pendingQuotes" :key="quote.id" :class="scoreClass(quote.recaptcha_score)">
          <td>
            <div class="d-flex">
              <b-button
                :variant="processingIds.includes(quote.id) ? 'secondary' : 'success'"
                class="mr-1"
                title="Approve"
                :disabled="processingIds.includes(quote.id)"
                @click="setQuoteStatus(quote, 'approved')"
              >✓
              </b-button>
              <b-button
                :variant="processingIds.includes(quote.id) ? 'secondary' : 'danger'"
                title="Dismiss"
                :disabled="processingIds.includes(quote.id)"
                @click="setQuoteStatus(quote, 'dismissed')"
              >✕
              </b-button>
            </div>
          </td>
          <td class="text-nowrap">{{ utcToLocalYMD(quote.date) }}</td>
          <td>{{ quote.school }}</td>
          <td>{{ quote.seats }}</td>
          <td>{{ quote.email }}</td>
          <td>{{ formatScore(quote.recaptcha_score) }}</td>
        </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-around align-items-center mb-2">
        <div>
          <div class="text-center">Showing {{ showingFrom }} – {{ showingTo }} of {{ pagination.total }}</div>
          <b-pagination-nav :value="pagination.page" :link-gen="paginationLink" :number-of-pages="totalPages" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
a.btn, span.btn {
  margin-left: 0.5rem;
}

.bg-yellow {
  background-color: #ffee55;
}

.bg-green {
  background-color: #bbeecc;
}

.bg-pink {
  background-color: pink;
}
</style>
